import { useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { useAuthStore } from "@/stores/auth.store";

export default function SsoCallback() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Azure may return params in query string or hash fragment depending on response_mode
    const queryParams = new URLSearchParams(search);
    const hashParams = new URLSearchParams(window.location.hash.replace("#", ""));
    const code = queryParams.get("code") || hashParams.get("code");
    const state = queryParams.get("state") || hashParams.get("state");
    const sessionState = queryParams.get("session_state") || hashParams.get("session_state");

    if (!code) {
      setError("Código de autenticação não encontrado");
      return;
    }

    const validate = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || "";
        const resp = await fetch(`${apiUrl}/auth/sso/callback`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code,
            state: state || "",
            sessionState: sessionState || "",
          }),
        });

        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          setError(data.message || "Falha na autenticação SSO");
          return;
        }

        const data = await resp.json();

        // Backend setou cookie HttpOnly via Set-Cookie. Apenas hidratar o store via /auth/me.
        if (data.accessToken || data.token) {
          await useAuthStore.getState().initialize();
          setLocation("/");
        } else if (data.email) {
          // Backend returned user info — need to create/find user and generate JWT
          // For now, redirect to login with SSO email pre-filled
          setLocation(`/login?sso_email=${encodeURIComponent(data.email)}`);
        } else {
          setError("Resposta SSO inesperada");
        }
      } catch {
        setError("Erro ao validar autenticação SSO");
      }
    };

    validate();
  }, [search, setLocation]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center max-w-sm mx-4">
          <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-neutral-900 mb-2">Erro no login SSO</h2>
          <p className="text-sm text-neutral-500 mb-4">{error}</p>
          <a href="/login" className="text-sm font-semibold text-blue-600 hover:underline">
            Voltar para o login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-neutral-500">Autenticando via SSO...</p>
      </div>
    </div>
  );
}
